import React, { useContext, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import AdminLayout from "../../layouts/AdminLayout";
import {
  getGastos, createGasto, addGastoPago, cancelGasto,
  getCategoriasGasto, getIvaCategorias, getMetodosPago, getProveedores,
} from "../../controllers/api_compras";
import { MONEDAS, monedaCodigo, fmt, handleApiError, hoyISO, upper } from "./comprasShared";
import {
  Card, PageHeader, Table, Th, Td, Modal, Field, TextInput, SelectInput,
  Toggle, BtnPrimary, BtnGhost, IconBtn, Badge,
} from "./ui";

// (2026-06-09) Pill de estatus con la semantica de color de la guia de diseno
// (seccion 1: estados). El estatus del gasto es multi-valor (no booleano), por
// eso usa un pill coloreado en vez del Badge Si/No.
const ESTATUS_PILL = {
  registrado: { cls: "bg-slate-100 text-slate-700", label: "REGISTRADO" },
  pagado_parcial: { cls: "bg-amber-100 text-amber-800", label: "PAGADO PARCIAL" },
  pagado_total: { cls: "bg-emerald-400 text-white", label: "PAGADO TOTAL" },
  anulado: { cls: "bg-red-400 text-white", label: "ANULADO" },
};
const EstatusPill = ({ estatus }) => {
  const p = ESTATUS_PILL[estatus] || { cls: "bg-slate-100 text-slate-700", label: String(estatus || "").toUpperCase() };
  return (
    <span className={`inline-block px-2 py-1 text-xs font-bold rounded ${p.cls}`}>
      {p.label}
    </span>
  );
};

const EMPTY = {
  categoria_gasto_id: "", concepto: "", fecha: hoyISO(), moneda_id: 3, base: "",
  aplica_iva: false, iva_categoria_id: "", tiene_soporte_fiscal: false,
  numero_documento: "", numero_control: "", incluir_en_libro_compras: false,
  proveedor_id: "", observaciones: "",
};

export default function GastosPage() {
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [ivas, setIvas] = useState([]);
  const [metodos, setMetodos] = useState([]);
  const [provs, setProvs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [pago, setPago] = useState(null); // gasto seleccionado para pago
  const [pagoForm, setPagoForm] = useState({ metodo_pago_id: "", monto: "", moneda_id: 3, banco: "", referencia: "", fecha_pago: hoyISO() });
  const [pagoErrors, setPagoErrors] = useState({});

  const cargar = async () => { try { setRows(await getGastos()); } catch (e) { handleApiError(e); } };
  useEffect(() => {
    cargar();
    getCategoriasGasto({ activo: 1 }).then(setCats).catch(() => {});
    getIvaCategorias().then((d) => setIvas(Array.isArray(d) ? d : (d?.data || []))).catch(() => {});
    getMetodosPago().then((d) => setMetodos(Array.isArray(d) ? d : (d?.data || []))).catch(() => {});
    getProveedores({ activo: 1 }).then(setProvs).catch(() => {});
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // (2026-06-09) Validacion reforzada estilo maestro (FormProformas.validate):
  // arma newErrors (feedback inline via Field error) + errorToast (lista en un
  // unico toast.error) y devuelve booleano. No altera el payload ni las
  // llamadas API; solo bloquea el guardado cuando hay errores.
  const validate = () => {
    const newErrors = {};
    var errorToast = [];

    if (!form.categoria_gasto_id) {
      newErrors.categoria_gasto_id = "La categoría de gasto es obligatoria.";
      errorToast.push("- Debe seleccionar la categoría de gasto.");
    }
    if (!form.concepto || !String(form.concepto).trim()) {
      newErrors.concepto = "El concepto es obligatorio.";
      errorToast.push("- Debe indicar el concepto del gasto.");
    }
    if (form.base === "" || form.base === null || form.base === undefined) {
      newErrors.base = "El monto base es obligatorio.";
      errorToast.push("- Debe indicar el monto base del gasto.");
    } else if (isNaN(Number(form.base)) || Number(form.base) <= 0) {
      newErrors.base = "El monto base debe ser mayor que cero.";
      errorToast.push("- El monto base debe ser mayor que cero.");
    }
    if (!form.fecha) {
      newErrors.fecha = "La fecha es obligatoria.";
      errorToast.push("- Debe indicar la fecha del gasto.");
    }
    if (form.aplica_iva && !form.iva_categoria_id) {
      newErrors.iva_categoria_id = "La alícuota de IVA es obligatoria cuando aplica IVA.";
      errorToast.push("- Debe seleccionar la alícuota de IVA.");
    }
    if (form.tiene_soporte_fiscal) {
      if (!form.numero_documento || !String(form.numero_documento).trim()) {
        newErrors.numero_documento = "El Nº de documento es obligatorio con soporte fiscal.";
        errorToast.push("- Debe indicar el Nº de documento (soporte fiscal).");
      }
      if (!form.numero_control || !String(form.numero_control).trim()) {
        newErrors.numero_control = "El Nº de control es obligatorio con soporte fiscal.";
        errorToast.push("- Debe indicar el Nº de control (soporte fiscal).");
      }
    }

    setErrors(newErrors);
    if (errorToast.length > 0) {
      toast.error(<div>
        {errorToast.map((item) => (
          <span className="text-start">{item}<br/></span>
        ))}
      </div>);
    }
    return Object.keys(newErrors).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    try {
      const body = { ...form, proveedor_id: form.proveedor_id || null,
        iva_categoria_id: form.aplica_iva ? (form.iva_categoria_id || null) : null };
      await createGasto(body);
      toast.success("Gasto registrado."); setOpen(false); setForm(EMPTY); setErrors({}); cargar();
    } catch (e) { handleApiError(e); }
  };

  // (2026-06-10) Validacion canonica del modal de pago (mismo patron que
  // validate()): newErrors (feedback inline via Field error) + errorToast en
  // un unico toast.error. Banco y referencia quedan OPCIONALES porque los
  // pagos en efectivo no los llevan. No altera el payload ni la llamada API.
  const validatePago = () => {
    const newErrors = {};
    var errorToast = [];

    if (!pagoForm.metodo_pago_id) {
      newErrors.metodo_pago_id = "El método de pago es obligatorio.";
      errorToast.push("- Debe seleccionar el método de pago.");
    }
    if (pagoForm.monto === "" || pagoForm.monto === null || pagoForm.monto === undefined) {
      newErrors.monto = "El monto es obligatorio.";
      errorToast.push("- Debe indicar el monto del pago.");
    } else if (isNaN(Number(pagoForm.monto)) || Number(pagoForm.monto) <= 0) {
      newErrors.monto = "El monto debe ser mayor que cero.";
      errorToast.push("- El monto del pago debe ser mayor que cero.");
    }
    if (!pagoForm.moneda_id) {
      newErrors.moneda_id = "La moneda es obligatoria.";
      errorToast.push("- Debe seleccionar la moneda del pago.");
    }
    if (!pagoForm.fecha_pago) {
      newErrors.fecha_pago = "La fecha de pago es obligatoria.";
      errorToast.push("- Debe indicar la fecha de pago.");
    }

    setPagoErrors(newErrors);
    if (errorToast.length > 0) {
      toast.error(<div>
        {errorToast.map((item, i) => (
          <span key={i} className="text-start">{item}<br/></span>
        ))}
      </div>);
    }
    return Object.keys(newErrors).length === 0;
  };

  const guardarPago = async () => {
    if (!validatePago()) return;
    try {
      await addGastoPago(pago.id, pagoForm);
      toast.success("Pago agregado."); setPago(null); setPagoErrors({}); cargar();
    } catch (e) { handleApiError(e); }
  };

  const anular = async (r) => {
    const motivo = window.prompt(`Motivo de anulación del gasto "${r.concepto}":`, "Anulado");
    if (motivo === null) return;
    // (2026-06-10) Regla de oro: texto en MAYUSCULAS; ademas el motivo es
    // obligatorio (si queda vacio tras trim no se llama la API).
    const motivoUpper = String(motivo).toUpperCase().trim();
    if (!motivoUpper) {
      toast.error("- El motivo de anulación es obligatorio");
      return;
    }
    try { await cancelGasto(r.id, motivoUpper); cargar(); } catch (e) { handleApiError(e); }
  };

  return (
    <AdminLayout nombreUsuario={user?.nombre || "Usuario"}>
      <div className="mt-8 px-4 md:px-8">
        <div className="mx-auto w-full">
          <div className="flex flex-wrap">
            <div className="w-full lg:w-12/12">
              <Card>
                <PageHeader
                  title="Gastos"
                  icon="fa-receipt"
                  actions={
                    <BtnPrimary icon="fa-plus" onClick={() => { setForm(EMPTY); setErrors({}); setOpen(true); }}>
                      Nuevo gasto
                    </BtnPrimary>
                  }
                />

                <Table>
                  <thead>
                    <tr>
                      <Th>Fecha</Th>
                      <Th>Concepto</Th>
                      <Th className="text-right">Total</Th>
                      <Th className="text-center">Mon.</Th>
                      <Th className="text-center">En libro</Th>
                      <Th className="text-center">Estatus</Th>
                      <Th className="text-center">Acciones</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <Td className="text-center text-blueGray-400" colSpan={7}>Sin gastos.</Td>
                      </tr>
                    )}
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-blueGray-50 transition-colors">
                        <Td>{(r.fecha || "").slice(0, 10)}</Td>
                        <Td>{r.concepto}</Td>
                        <Td className="text-right">{fmt(r.total)}</Td>
                        <Td className="text-center">{monedaCodigo(r.moneda_id)}</Td>
                        <Td className="text-center"><Badge ok={!!r.incluir_en_libro_compras} /></Td>
                        <Td className="text-center"><EstatusPill estatus={r.estatus} /></Td>
                        <Td>
                          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.25rem", whiteSpace: "nowrap" }}>
                            {r.estatus !== "anulado" && r.estatus !== "pagado_total" && (
                              <IconBtn
                                icon="fa-money-bill-wave"
                                color="text-emerald-600 hover:text-emerald-800"
                                title="Pagar"
                                onClick={() => { setPago(r); setPagoForm({ metodo_pago_id: "", monto: "", moneda_id: r.moneda_id, banco: "", referencia: "", fecha_pago: hoyISO() }); setPagoErrors({}); }}
                              />
                            )}
                            {r.estatus !== "anulado" && (
                              <IconBtn
                                icon="fa-ban"
                                color="text-red-600 hover:text-red-800"
                                title="Anular"
                                onClick={() => anular(r)}
                              />
                            )}
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            </div>
          </div>
        </div>

        {/* Crear gasto */}
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          title="Nuevo gasto"
          icon="fa-receipt"
          maxWidth="max-w-[900px]"
          footer={
            <>
              <BtnGhost onClick={() => setOpen(false)}>Cancelar</BtnGhost>
              <BtnPrimary icon="fa-save" onClick={save}>Guardar</BtnPrimary>
            </>
          }
        >
          <div className="flex flex-wrap -mx-4">
            <Field label="CATEGORÍA *" className="w-full lg:w-6/12 px-4" error={errors.categoria_gasto_id}>
              <SelectInput
                value={form.categoria_gasto_id}
                onChange={(v) => setField("categoria_gasto_id", v)}
                placeholder="Seleccione…"
              >
                {cats.map((c) => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
              </SelectInput>
            </Field>

            <Field label="CONCEPTO *" className="w-full lg:w-6/12 px-4" error={errors.concepto}>
              <TextInput value={form.concepto} onChange={(v) => setField("concepto", upper(v))} />
            </Field>

            <Field label="FECHA *" className="w-full lg:w-6/12 px-4" error={errors.fecha}>
              <TextInput type="date" value={form.fecha} onChange={(v) => setField("fecha", v)} />
            </Field>

            <Field label="MONEDA" className="w-full lg:w-6/12 px-4">
              <SelectInput value={form.moneda_id} onChange={(v) => setField("moneda_id", v)}>
                {MONEDAS.map((m) => <option key={m.id} value={m.id}>{m.codigo}</option>)}
              </SelectInput>
            </Field>

            <Field label="MONTO BASE *" className="w-full lg:w-6/12 px-4" error={errors.base}>
              <TextInput type="number" value={form.base} onChange={(v) => setField("base", v)} />
              <p className="text-blueGray-400 text-xs mt-1">EN LA MONEDA ELEGIDA (SIN IVA)</p>
            </Field>

            <Field label="PROVEEDOR (OPCIONAL)" className="w-full lg:w-6/12 px-4">
              <SelectInput value={form.proveedor_id} onChange={(v) => setField("proveedor_id", v)} placeholder="(ninguno)">
                {provs.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </SelectInput>
            </Field>

            <Field label="APLICA IVA" className="w-full lg:w-6/12 px-4">
              <div className="py-2">
                <Toggle checked={form.aplica_iva} onChange={(v) => setField("aplica_iva", v)} label="Aplica IVA" />
              </div>
            </Field>

            {form.aplica_iva ? (
              <Field label="ALÍCUOTA IVA *" className="w-full lg:w-6/12 px-4" error={errors.iva_categoria_id}>
                <SelectInput value={form.iva_categoria_id} onChange={(v) => setField("iva_categoria_id", v)} placeholder="Seleccione…">
                  {ivas.map((i) => <option key={i.id} value={i.id}>{i.nombre} ({i.tasa_porcentaje}%)</option>)}
                </SelectInput>
              </Field>
            ) : (
              <div className="w-full lg:w-6/12 px-4" />
            )}

            <Field label="TIENE SOPORTE FISCAL" className="w-full lg:w-6/12 px-4">
              <div className="py-2">
                <Toggle checked={form.tiene_soporte_fiscal} onChange={(v) => setField("tiene_soporte_fiscal", v)} label="Tiene soporte fiscal (factura)" />
              </div>
            </Field>

            <Field label="LIBRO DE COMPRAS" className="w-full lg:w-6/12 px-4">
              <div className="py-2">
                <Toggle checked={form.incluir_en_libro_compras} onChange={(v) => setField("incluir_en_libro_compras", v)} label="Incluir en Libro de Compras" />
              </div>
            </Field>

            <Field
              label={form.tiene_soporte_fiscal ? "Nº DOCUMENTO *" : "Nº DOCUMENTO"}
              className="w-full lg:w-6/12 px-4"
              error={errors.numero_documento}
            >
              <TextInput value={form.numero_documento} onChange={(v) => setField("numero_documento", v)} />
            </Field>

            <Field
              label={form.tiene_soporte_fiscal ? "Nº CONTROL *" : "Nº CONTROL (SI FACTURA)"}
              className="w-full lg:w-6/12 px-4"
              error={errors.numero_control}
            >
              <TextInput value={form.numero_control} onChange={(v) => setField("numero_control", v)} />
            </Field>
          </div>
        </Modal>

        {/* Pago */}
        <Modal
          open={!!pago}
          onClose={() => setPago(null)}
          title="Pago del gasto"
          icon="fa-money-bill-wave"
          maxWidth="max-w-[640px]"
          footer={
            <>
              <BtnGhost onClick={() => setPago(null)}>Cancelar</BtnGhost>
              <BtnPrimary icon="fa-check" onClick={guardarPago}>Registrar pago</BtnPrimary>
            </>
          }
        >
          <div className="flex flex-wrap -mx-4">
            <Field label="MÉTODO DE PAGO *" className="w-full lg:w-6/12 px-4" error={pagoErrors.metodo_pago_id}>
              <SelectInput
                value={pagoForm.metodo_pago_id}
                onChange={(v) => setPagoForm((f) => ({ ...f, metodo_pago_id: v }))}
                placeholder="Seleccione…"
              >
                {metodos.map((m) => <option key={m.id} value={m.id}>{m.nombre}{m.aplica_igtf ? " (IGTF)" : ""}</option>)}
              </SelectInput>
            </Field>

            <Field label="MONEDA *" className="w-full lg:w-6/12 px-4" error={pagoErrors.moneda_id}>
              <SelectInput value={pagoForm.moneda_id} onChange={(v) => setPagoForm((f) => ({ ...f, moneda_id: v }))}>
                {MONEDAS.map((m) => <option key={m.id} value={m.id}>{m.codigo}</option>)}
              </SelectInput>
            </Field>

            <Field label="MONTO *" className="w-full lg:w-6/12 px-4" error={pagoErrors.monto}>
              <TextInput type="number" value={pagoForm.monto} onChange={(v) => setPagoForm((f) => ({ ...f, monto: v }))} />
            </Field>

            <Field label="FECHA DE PAGO *" className="w-full lg:w-6/12 px-4" error={pagoErrors.fecha_pago}>
              <TextInput type="date" value={pagoForm.fecha_pago} onChange={(v) => setPagoForm((f) => ({ ...f, fecha_pago: v }))} />
            </Field>

            <Field label="BANCO" className="w-full lg:w-6/12 px-4">
              <TextInput value={pagoForm.banco} onChange={(v) => setPagoForm((f) => ({ ...f, banco: v }))} />
            </Field>

            <Field label="REFERENCIA" className="w-full lg:w-6/12 px-4">
              <TextInput value={pagoForm.referencia} onChange={(v) => setPagoForm((f) => ({ ...f, referencia: v }))} />
            </Field>
          </div>

          <p className="text-xs text-blueGray-400 mt-2 px-4">
            <i className="fas fa-info-circle mr-1" aria-hidden="true" />
            El IGTF se calcula automáticamente si el método aplica y el pago es en divisa.
          </p>
        </Modal>

        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </AdminLayout>
  );
}
